import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase, supabaseReady } from './supabase'
import { initPush } from './push'

/**
 * 'none' = signed in, but neither a coach nor linked to an athlete (or the
 * lookup failed). Previously this fell through to 'coach', so an athlete whose
 * record lookup hit a network blip landed on the coach dashboard.
 */
type Role = 'coach' | 'athlete' | 'none' | null

interface SessionState {
  session: Session | null
  loading: boolean
  /** Resolved after login: coaches manage; athletes see only their own workout. */
  role: Role
  /** The signed-in athlete's own athletes.id (when role === 'athlete'). */
  athleteId: string | null
  roleResolved: boolean
  ready: boolean
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
}

const Ctx = createContext<SessionState | undefined>(undefined)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(supabaseReady)
  const [role, setRole] = useState<Role>(null)
  const [athleteId, setAthleteId] = useState<string | null>(null)
  const [roleResolved, setRoleResolved] = useState(false)

  useEffect(() => {
    if (!supabaseReady) return
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setLoading(false)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => sub.subscription.unsubscribe()
  }, [])

  // Determine role once we have a user: an athlete has a row in `athletes`
  // matched by auth_user_id (RLS returns only their own); everyone else is a coach.
  useEffect(() => {
    let cancelled = false
    const uid = session?.user?.id
    if (!uid) {
      setRole(null)
      setAthleteId(null)
      setRoleResolved(Boolean(supabaseReady) ? !session : true)
      return
    }
    setRoleResolved(false)
    ;(async () => {
      // Coach status wins — an account in the coaches table is always a coach,
      // even if it also has an athlete record.
      const { data: coach, error: coachErr } = await supabase.rpc('is_coach')
      if (cancelled) return
      if (coach === true) {
        setRole('coach')
        setAthleteId(null)
        setRoleResolved(true)
        return
      }
      const { data, error } = await supabase
        .from('athletes')
        .select('id')
        .eq('auth_user_id', uid)
        .maybeSingle()
      if (cancelled) return
      if (data?.id) {
        setRole('athlete')
        setAthleteId(data.id)
      } else {
        // Unknown account, or a lookup failure: never assume coach.
        if (coachErr || error) console.error('Role lookup failed:', (coachErr || error)?.message)
        setRole('none')
        setAthleteId(null)
      }
      setRoleResolved(true)
    })()
    return () => {
      cancelled = true
    }
  }, [session])

  // Once role is known, register this device for push (native only; no-op on web).
  useEffect(() => {
    const uid = session?.user?.id
    if (uid && roleResolved) initPush(uid, athleteId)
  }, [session?.user?.id, roleResolved, athleteId])

  const signIn: SessionState['signIn'] = async (email, password) => {
    if (!supabaseReady) return { error: 'Supabase not configured' }
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error: error?.message ?? null }
  }

  const signOut = async () => {
    if (supabaseReady) await supabase.auth.signOut()
    setSession(null)
    setRole(null)
    setAthleteId(null)
  }

  return (
    <Ctx.Provider
      value={{ session, loading, role, athleteId, roleResolved, ready: supabaseReady, signIn, signOut }}
    >
      {children}
    </Ctx.Provider>
  )
}

export function useSession() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useSession must be used within SessionProvider')
  return ctx
}
